/**
 * 部署后旧页面加载不到懒加载分块（2026-10-05 leo：「经常遇到」）。
 *
 * 构建产物的文件名带内容哈希，每次部署整批替换、旧文件随之消失。部署前打开的页面里记的还是旧文件名，
 * 之后点进一个没加载过的页面（React.lazy），就是 `Failed to fetch dynamically imported module: …/ActEventPage-<旧哈希>.js`。
 * 这不是 bug，刷新一次拿到新的入口就好；「重试」没用——失败的 import() 被浏览器缓存，再 import 还是同一个拒绝。
 *
 * 做法：认出这类错误就整页刷新一次。刷新前不用自己判断有没有未保存改动——App 挂着 beforeunload，
 * 有草稿时浏览器会先问「要离开吗」，运营点取消就留在原页（错误页上还有手动刷新按钮）。
 * 防循环：同一个会话里 30 秒内只自动刷一次；刷完还是拿不到（真的缺文件、断网）就停在错误页，不来回刷。
 */

/** Chrome / Firefox / Safari 三种说法 */
const PATTERNS = [
  /Failed to fetch dynamically imported module/i,
  /error loading dynamically imported module/i,
  /Importing a module script failed/i,
];

export function isStaleChunkError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String((error as { message?: unknown } | null)?.message ?? error ?? "");
  return PATTERNS.some((re) => re.test(message));
}

const KEY = "admin:stale-chunk-reload-at";
const WINDOW_MS = 30_000;

/**
 * 该不该自动刷新：上次自动刷新超过 30 秒（或从没刷过）才刷，并记下这一次。
 * sessionStorage 读写失败（隐私模式等）时不自动刷——宁可停在有按钮的错误页，也不冒无限刷新的险
 */
export function claimAutoReload(now: number, storage: Pick<Storage, "getItem" | "setItem"> | null): boolean {
  if (!storage) return false;
  try {
    const last = Number(storage.getItem(KEY) ?? 0);
    if (Number.isFinite(last) && now - last < WINDOW_MS) return false;
    storage.setItem(KEY, String(now));
    return true;
  } catch {
    return false;
  }
}

function sessionStore(): Storage | null {
  try { return window.sessionStorage; } catch { return null; }
}

/** 认出来且没在 30 秒内刷过 → 整页刷新，返回 true；否则返回 false，由调用方显示错误页 */
export function reloadForStaleChunk(error: unknown): boolean {
  if (!isStaleChunkError(error)) return false;
  if (!claimAutoReload(Date.now(), sessionStore())) return false;
  window.location.reload();
  return true;
}

/**
 * Vite 在动态 import 的预加载失败时派发 `vite:preloadError`（https://vite.dev/guide/build#load-error-handling）。
 * 在这里先接住，比等 React 错误边界更早，页面不会先闪一下错误页
 */
export function installStaleChunkReload(): void {
  window.addEventListener("vite:preloadError", (event) => {
    const payload = (event as Event & { payload?: unknown }).payload;
    if (reloadForStaleChunk(payload ?? new Error("Failed to fetch dynamically imported module"))) event.preventDefault();
  });
}
